import React, { useState, useRef, useCallback, useLayoutEffect } from 'react';
import { motion } from 'framer-motion';

const SNAP_SPOTS = [
  { xPct: 0.10, edge: 'top' },    // top-left
  { xPct: 0.50, edge: 'top' },    // top-center
  { xPct: 0.90, edge: 'top' },    // top-right
  { xPct: 0.10, edge: 'bottom' }, // bottom-left
  { xPct: 0.50, edge: 'bottom' }, // bottom-center
  { xPct: 0.90, edge: 'bottom' }, // bottom-right
] as const;

const EDGE_INSET = 16;
const DEFAULT_SPOT = 4; // bottom-center

function getSnapXY(idx: number, elW: number, elH: number) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const spot = SNAP_SPOTS[idx];
  const maxX = vw - EDGE_INSET - elW;
  const x = Math.max(EDGE_INSET, Math.min(maxX, spot.xPct * vw - elW / 2));
  const y = spot.edge === 'top' ? EDGE_INSET : vh - EDGE_INSET - elH;
  return { x, y };
}

function findClosestSpotIndex(cx: number, cy: number): number {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let best = 0;
  let minDist = Infinity;
  for (let i = 0; i < SNAP_SPOTS.length; i++) {
    const s = SNAP_SPOTS[i];
    const sx = s.xPct * vw;
    const sy = s.edge === 'top' ? EDGE_INSET + 20 : vh - EDGE_INSET - 20;
    const d = Math.hypot(cx - sx, cy - sy);
    if (d < minDist) { minDist = d; best = i; }
  }
  return best;
}

interface FloatingBarProps {
  children: React.ReactNode;
}

const FloatingBar: React.FC<FloatingBarProps> = ({ children }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [spotIdx, setSpotIdx] = useState(DEFAULT_SPOT);
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const [isSnapping, setIsSnapping] = useState(false);
  const dragging = useRef(false);
  const dragStart = useRef({ mx: 0, my: 0, ex: 0, ey: 0 });

  // Measure and set initial position
  useLayoutEffect(() => {
    const el = measureRef.current ?? containerRef.current;
    if (!el || pos !== null) return;
    setPos(getSnapXY(spotIdx, el.offsetWidth, el.offsetHeight));
  }, [spotIdx, pos]);

  // Resize handler
  useLayoutEffect(() => {
    const onResize = () => {
      const el = containerRef.current;
      if (!el || dragging.current) return;
      setPos(getSnapXY(spotIdx, el.offsetWidth, el.offsetHeight));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [spotIdx]);

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    if ((e.target as HTMLElement).closest('button, a')) return;
    if (!pos) return;

    dragging.current = true;
    setIsSnapping(false);
    dragStart.current = { mx: e.clientX, my: e.clientY, ex: pos.x, ey: pos.y };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    e.preventDefault();
  }, [pos]);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragging.current) return;
    setPos({
      x: dragStart.current.ex + (e.clientX - dragStart.current.mx),
      y: dragStart.current.ey + (e.clientY - dragStart.current.my),
    });
  }, []);

  const onPointerUp = useCallback(() => {
    if (!dragging.current) return;
    dragging.current = false;

    const el = containerRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const newIdx = findClosestSpotIndex(rect.left + rect.width / 2, rect.top + rect.height / 2);
    const snapTarget = getSnapXY(newIdx, el.offsetWidth, el.offsetHeight);

    setIsSnapping(true);
    setSpotIdx(newIdx);
    setPos(snapTarget);
  }, []);

  // Hidden measure element for initial render
  if (pos === null) {
    return (
      <div
        ref={measureRef}
        style={{ position: 'fixed', visibility: 'hidden', pointerEvents: 'none' }}
      >
        <div className="flex items-center gap-2">
          {children}
        </div>
      </div>
    );
  }

  return (
    <motion.div
      ref={containerRef}
      animate={{ left: pos.x, top: pos.y }}
      transition={isSnapping ? { type: 'spring', stiffness: 300, damping: 30 } : { duration: 0 }}
      style={{
        position: 'fixed',
        zIndex: 999,
        cursor: dragging.current ? 'grabbing' : 'grab',
      }}
      onAnimationComplete={() => setIsSnapping(false)}
      className="pointer-events-auto"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
    >
      {children}
    </motion.div>
  );
};

export default FloatingBar;
